import type { Fixture, MarketTeams } from '@h2h/shared';
import { Check, Lock } from '../../components/ui/icons';
import type { DuelSidePrediction } from '../../lib/api';
import { cn } from '../../lib/cn';
import { signedPoints } from '../../lib/format';
import { isRevealed, pickMultiplier, pickSummary, teamCode } from './helpers';

export function PickByPick({
  fixture,
  mine,
  theirs,
  myPoints,
  theirPoints,
  rivalName,
  onOpen
}: {
  fixture: Fixture | undefined;
  mine: DuelSidePrediction | undefined;
  theirs: DuelSidePrediction | undefined;
  myPoints: number | null;
  theirPoints: number | null;
  rivalName: string;
  onOpen?: () => void;
}) {
  const final = fixture?.status === 'SETTLED' && fixture.result;
  const home = fixture ? teamCode(fixture.homeTeam, fixture.homeCode) : '?';
  const away = fixture ? teamCode(fixture.awayTeam, fixture.awayCode) : '?';
  const label = final
    ? `${home} ${fixture!.result!.homeGoals}–${fixture!.result!.awayGoals} ${away}`
    : `${home} v ${away}`;
  const live = fixture?.status === 'LIVE';

  const iWin = myPoints != null && theirPoints != null && myPoints > theirPoints;
  const theyWin = myPoints != null && theirPoints != null && theirPoints > myPoints;

  return (
    <div className="overflow-hidden rounded-2xl border border-black/5 bg-card shadow-tile">
      <button
        type="button"
        onClick={onOpen}
        disabled={!onOpen}
        className={cn(
          'flex w-full items-center justify-center gap-[9px] border-b border-black/[0.04] bg-wash p-[11px]',
          onOpen && 'tap'
        )}
      >
        <span className="num text-[13px] font-extrabold tracking-snug text-ink">{label}</span>
        {final ? (
          <span className="text-[10px] font-bold tracking-wide text-ink3">FT</span>
        ) : live ? (
          <span className="inline-flex items-center gap-[5px] rounded-md bg-accent/20 px-[7px] py-[2px] text-[10px] font-extrabold tracking-wide text-accent">
            <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent" /> LIVE
          </span>
        ) : null}
      </button>
      <div className="flex">
        <SideCell label="YOU" pick={mine} points={myPoints} win={iWin} highlight="accent" teams={{ home, away }} />
        <div className="w-px bg-black/5" />
        <SideCell
          label={rivalName.split(/\s+/)[0].toUpperCase()}
          pick={theirs}
          points={theirPoints}
          win={theyWin}
          highlight="rival"
          teams={{ home, away }}
        />
      </div>
    </div>
  );
}

function SideCell({
  label,
  pick,
  points,
  win,
  highlight,
  teams
}: {
  label: string;
  pick: DuelSidePrediction | undefined;
  points: number | null;
  win: boolean;
  highlight: 'accent' | 'rival';
  teams: MarketTeams;
}) {
  const locked = !pick || pick.locked || !isRevealed(pick);
  return (
    <div
      className={cn(
        'flex-1 p-[14px]',
        win && highlight === 'accent' && 'bg-accent/[0.06]',
        win && highlight === 'rival' && 'bg-[#5B6EE1]/[0.07]'
      )}
    >
      <div className="flex items-center justify-between">
        <span
          className={cn(
            'text-[10px] font-extrabold tracking-wide',
            win ? (highlight === 'accent' ? 'text-accent-ink' : 'text-[#5B6EE1]') : 'text-ink2'
          )}
        >
          {label}
        </span>
        {win && <Check width={14} height={14} strokeWidth={3} className={highlight === 'accent' ? 'text-accent' : 'text-[#5B6EE1]'} />}
      </div>
      {locked ? (
        <div className="mt-2 flex items-center gap-[6px] text-ink2">
          <Lock width={13} height={13} strokeWidth={2.2} />
          <span className="text-[12px] font-semibold">Reveals at kickoff</span>
        </div>
      ) : (
        <>
          <div className="mt-2 text-[12px] font-bold text-ink">
            {pickSummary(pick!, teams)}{' '}
            <span className="font-semibold text-ink2">· ×{pickMultiplier(pick!)}</span>
          </div>
          {points != null && (
            <div
              className={cn(
                'num mt-1 text-[18px] font-extrabold',
                highlight === 'accent' ? 'text-accent-ink' : 'text-[#5B6EE1]'
              )}
            >
              {signedPoints(points)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
